import { Component, OnInit, Inject, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { EquipmentService } from '@app/services/equipment.service';
import {
  Equipment,
  EquipmentPurchaseHistory,
  EquipmentPartPackage,
  EquipmentPart,
  EquipmentImage
} from '@app/model/equipment';
import { ToastrService } from 'ngx-toastr';
import { AddEquipmentDialogComponent } from '../add-equipment-dialog/add-equipment-dialog.component';
import { CartProductService } from '@app/api/api';
import { forkJoin } from 'rxjs';
import { environment } from 'environments/environment';

declare var $: any;

interface PurchasedPackageGroup {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  parts: EquipmentPart[];
  subtotal: number;
}

interface OrderPartGroups {
  packages: PurchasedPackageGroup[];
  looseParts: EquipmentPart[];
}

@Component({
  selector: 'app-equipment-detail-dialog',
  templateUrl: './equipment-detail-dialog.component.html',
  styleUrls: ['./equipment-detail-dialog.component.scss']
})
export class EquipmentDetailDialogComponent implements OnInit, OnDestroy {
  equipment: Equipment;
  purchaseHistory: EquipmentPurchaseHistory[] = [];
  historyPackageGroups: OrderPartGroups[] = [];
  partPackages: EquipmentPartPackage[] = [];
  activeTab: 'history' | 'packages' = 'history';
  isLoadingHistory = true;
  isLoadingPackages = true;
  // [HIDDEN] Compatible Parts - temporarily hide, re-enable when needed
  // isLoadingCompatible = true;
  // compatibleProducts: EquipmentPart[] = [];
  showFullDetail = false;
  showPurchaseDetail = false;
  isSummaryExpanded = false;
  expandedPackageId: string | null = null;
  isUploadingImage = false;
  isUploadingImages = false;
  hasUploadedImage = false;
  isUpdatingStatus = false;
  equipmentImages: EquipmentImage[] = [];
  selectedGalleryIndex = 0;
  isDeletingImage = false;

  statusOptions = ['active', 'inactive', 'maintenance', 'retired'];

  private subs: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<EquipmentDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Equipment,
    private equipmentService: EquipmentService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private cartProductService: CartProductService
  ) {
    this.equipment = data;
    this.subs.push(
      this.dialogRef.backdropClick().subscribe(() => {
        this.dialogRef.close({ refresh: this.hasUploadedImage });
      })
    );
  }

  ngOnInit(): void {
    // Fetch fresh equipment data from API to ensure all fields are up-to-date
    const sub = this.equipmentService.fetchEquipmentById(this.equipment.id).subscribe(fresh => {
      this.equipment = fresh;
      this.equipmentImages = fresh.extendImages || [];
      this.cdr.detectChanges();
    });
    this.subs.push(sub);

    this.loadPurchaseHistory();
    this.loadPartPackages();
    // [HIDDEN] Compatible Parts - temporarily hide, re-enable when needed
    // this.loadCompatibleProducts();
  }

  loadEquipmentImages(): void {
    if (this.equipment && this.equipment.extendImages) {
      this.equipmentImages = this.equipment.extendImages;
    } else {
      this.equipmentImages = [];
    }
    this.cdr.detectChanges();
  }

  refreshEquipmentData(): void {
    const sub = this.equipmentService.fetchEquipmentById(this.equipment.id).subscribe(fresh => {
      this.equipment = fresh;
      this.equipmentImages = fresh.extendImages || [];
      this.cdr.detectChanges();
    });
    this.subs.push(sub);
  }

  getFullImagePath(path: string): string {
    if (!path) return '/assets/media/products/placeholder.png';
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('assets/')) {
      return path;
    }
    const host = environment.backendhost || '';
    const cleanHost = host.endsWith('/') ? host.slice(0, -1) : host;
    const cleanPath = path.startsWith('/') ? path : '/' + path;
    return cleanHost + cleanPath;
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  loadPurchaseHistory(): void {
    const sub = this.equipmentService.getPurchaseHistory(this.equipment.id).subscribe((history) => {
      this.purchaseHistory = history;
      this.historyPackageGroups = history.map((order) => this.groupOrderParts(order));
      this.isLoadingHistory = false;
      this.cdr.detectChanges();
    });
    this.subs.push(sub);
  }

  private groupOrderParts(order: EquipmentPurchaseHistory): OrderPartGroups {
    const packagesMap = new Map<string, PurchasedPackageGroup>();
    const looseParts: EquipmentPart[] = [];

    order.parts.forEach((part) => {
      if (part.sourceType === 'part_package_item' && part.partPackageId) {
        let group = packagesMap.get(part.partPackageId);
        if (!group) {
          group = {
            id: part.partPackageId,
            name: part.partPackageName || '',
            description: part.partPackageDescription || '',
            imageUrl: part.partPackageImage || '',
            parts: [],
            subtotal: 0
          };
          packagesMap.set(part.partPackageId, group);
        }
        group.parts.push(part);
        group.subtotal += part.price * (part.quantity || 1);
      } else {
        looseParts.push(part);
      }
    });

    return { packages: Array.from(packagesMap.values()), looseParts };
  }

  loadPartPackages(): void {
    const sub = this.equipmentService.getPartPackages(this.equipment.id).subscribe((packages) => {
      this.partPackages = packages;
      this.isLoadingPackages = false;
      this.cdr.detectChanges();
    });
    this.subs.push(sub);
  }

  // [HIDDEN] Compatible Parts - temporarily hide, re-enable when needed
  // loadCompatibleProducts(): void {
  //   const sub = this.equipmentService
  //     .getCompatibleProducts(this.equipment.id)
  //     .subscribe((products) => {
  //       this.compatibleProducts = products;
  //       this.isLoadingCompatible = false;
  //       this.cdr.detectChanges();
  //     });
  //   this.subs.push(sub);
  // }

  switchTab(tab: 'history' | 'packages'): void {
    this.activeTab = tab;
    this.cdr.detectChanges();
  }

  togglePackage(packageId: string): void {
    this.expandedPackageId = this.expandedPackageId === packageId ? null : packageId;
    this.cdr.detectChanges();
  }

  toggleSummary(): void {
    this.isSummaryExpanded = !this.isSummaryExpanded;
    this.cdr.detectChanges();
  }

  openFullDetail(): void {
    this.showFullDetail = true;
    this.cdr.detectChanges();
  }

  closeFullDetail(): void {
    this.showFullDetail = false;
    this.cdr.detectChanges();
  }

  openPurchaseDetail(tab: 'history' | 'packages' = 'history'): void {
  this.activeTab = tab;
  this.showPurchaseDetail = true;
  this.cdr.detectChanges();
}

closePurchaseDetail(): void {
  this.showPurchaseDetail = false;
  this.cdr.detectChanges();
}

  addPartToCart(part: EquipmentPart): void {
    if (!part.id) {
      this.toastr.error('Sản phẩm không có ID hợp lệ', 'Lỗi');
      return;
    }
    const qty = part.quantity || 1;
    const myEqId = this.equipment.id;
    const srcType = part.sourceType || undefined;
    const srcRefId = part.sourceRefId || undefined;
    // If the product has a classification, pass subTypeId (= product_type_value.id);
    // otherwise pass undefined so the API ignores it.
    const subTypeId = part.productTypeId || undefined;

    this.cartProductService.apiMyCartProductsPost(part.id, qty, subTypeId, myEqId, srcType, srcRefId).subscribe(
      (res) => {
        if (res && res.success) {
          this.toastr.success(`Đã thêm "${part.name}" vào giỏ hàng`, 'Thành công');
          this.updateCartCount();
        } else {
          this.toastr.error('Thêm vào giỏ hàng thất bại', 'Lỗi');
        }
      },
      (err) => {
        console.error('Lỗi thêm giỏ hàng:', err);
        this.toastr.error('Lỗi kết nối máy chủ', 'Lỗi');
      }
    );
  }

  addPackageToCart(pkg: EquipmentPartPackage): void {
    if (!pkg.parts || pkg.parts.length === 0) {
      this.toastr.warning('Gói sản phẩm này không có sản phẩm nào', 'Cảnh báo');
      return;
    }

    const selectedParts = pkg.parts.filter((p) => p.selected);
    if (selectedParts.length === 0) {
      this.toastr.warning('Vui lòng chọn ít nhất một sản phẩm trong gói', 'Cảnh báo');
      return;
    }

    const myEqId = this.equipment.id;
    const observables = selectedParts.map((part) => {
      const qty = part.quantity || 1;
      const srcType = part.sourceType || 'part_package_item';
      const srcRefId = part.sourceRefId || undefined;
      // If the product has a classification, pass subTypeId (= product_type_value.id);
      // otherwise pass undefined so the API ignores it.
      const subTypeId = part.productTypeId || undefined;
      return this.cartProductService.apiMyCartProductsPost(part.id, qty, subTypeId, myEqId, srcType, srcRefId);
    });

    forkJoin(observables).subscribe(
      (responses) => {
        this.toastr.success(
          `Đã thêm ${selectedParts.length}/${pkg.parts.length} sản phẩm của gói "${pkg.name}" vào giỏ hàng`,
          'Thành công'
        );
        this.updateCartCount();
      },
      (err) => {
        console.error('Lỗi khi thêm gói vào giỏ hàng:', err);
        this.toastr.error('Một số hoặc toàn bộ sản phẩm không thể thêm vào giỏ hàng', 'Lỗi');
        this.updateCartCount();
      }
    );
  }

  togglePartSelection(part: EquipmentPart, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    part.selected = !part.selected;
    this.cdr.detectChanges();
  }

  toggleSelectAllParts(pkg: EquipmentPartPackage, event?: any): void {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    // If event is a MatCheckboxChange, use its checked state; otherwise toggle.
    const targetChecked =
      event && typeof event.checked === 'boolean' ? event.checked : null;
    const allSelected = this.isAllPartsSelected(pkg);
    const next = targetChecked !== null ? targetChecked : !allSelected;
    pkg.parts.forEach((p) => (p.selected = next));
    this.cdr.detectChanges();
  }

  isAllPartsSelected(pkg: EquipmentPartPackage): boolean {
    return !!pkg.parts && pkg.parts.length > 0 && pkg.parts.every((p) => p.selected);
  }

  isSomePartsSelected(pkg: EquipmentPartPackage): boolean {
    return !!pkg.parts && pkg.parts.some((p) => p.selected) && !this.isAllPartsSelected(pkg);
  }

  getSelectedCount(pkg: EquipmentPartPackage): number {
    return pkg.parts ? pkg.parts.filter((p) => p.selected).length : 0;
  }

  getSelectedTotal(pkg: EquipmentPartPackage): number {
    if (!pkg.parts) return 0;
    return pkg.parts
      .filter((p) => p.selected)
      .reduce((sum, part) => sum + part.price * (part.quantity || 1), 0);
  }

  reorderFromHistory(history: EquipmentPurchaseHistory): void {
    if (!history.parts || history.parts.length === 0) {
      this.toastr.warning('Lịch sử mua hàng này không có sản phẩm nào', 'Cảnh báo');
      return;
    }

    const myEqId = this.equipment.id;
    const observables = history.parts.map((part) => {
      const qty = part.quantity || 1;
      const srcType = part.sourceType || undefined;
      const srcRefId = part.sourceRefId || undefined;
      // If the product has a classification, pass subTypeId (= product_type_value.id);
      // otherwise pass undefined so the API ignores it.
      const subTypeId = part.productTypeId || undefined;
      return this.cartProductService.apiMyCartProductsPost(part.id, qty, subTypeId, myEqId, srcType, srcRefId);
    });

    forkJoin(observables).subscribe(
      (responses) => {
        this.toastr.success(
          `Đã đặt lại thành công ${history.parts.length} sản phẩm vào giỏ hàng`,
          'Đặt lại đơn hàng'
        );
        this.updateCartCount();
      },
      (err) => {
        console.error('Lỗi khi đặt lại đơn hàng:', err);
        this.toastr.error('Không thể đặt lại toàn bộ sản phẩm vào giỏ hàng', 'Lỗi');
        this.updateCartCount();
      }
    );
  }

  private updateCartCount(): void {
    this.cartProductService.apiMyCartProductsGet().subscribe((reslst) => {
      if (reslst && reslst.success && reslst.data && reslst.data.collection) {
        const count = reslst.data.collection.length;
        if (count === 0) {
          $('.numberOfProduct').text('');
        } else {
          $('.numberOfProduct').text(count);
        }
      }
    });
  }

  formatPrice(price: number): string {
    return price.toLocaleString('vi-VN');
  }

  formatDate(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('vi-VN');
  }

  onUploadAvatarImage(event: any): void {
    const files: File[] = Array.from(event.target.files);
    if (!files || files.length === 0) return;

    const file = files[0];
    this.isUploadingImage = true;
    this.cdr.detectChanges();

    const sub = this.equipmentService.setDefaultImage(this.equipment.id, file).subscribe(
      (res) => {
        this.isUploadingImage = false;
        this.hasUploadedImage = true;
        this.equipment.imageUrl = res.default_image || res.imageUrl || res.url || this.equipment.imageUrl;
        this.toastr.success('Đã cập nhật ảnh đại diện', 'Thành công');
        this.cdr.detectChanges();
      },
      (err) => {
        this.isUploadingImage = false;
        console.error('Lỗi khi tải ảnh đại diện lên:', err);
        this.toastr.error('Không thể cập nhật ảnh đại diện', 'Lỗi');
        this.cdr.detectChanges();
      }
    );
    this.subs.push(sub);

    event.target.value = '';
  }

  onUploadGalleryImages(event: any): void {
    const files: File[] = Array.from(event.target.files);
    if (!files || files.length === 0) return;

    this.isUploadingImage = true;
    this.cdr.detectChanges();

    const sub = this.equipmentService.uploadEquipmentImages(this.equipment.id, files).subscribe(
      (results) => {
        this.isUploadingImage = false;
        this.hasUploadedImage = true;
        this.toastr.success(`Đã tải lên ${files.length} ảnh vào thư viện`, 'Thành công');
        this.refreshEquipmentData();
        this.cdr.detectChanges();
      },
      (err) => {
        this.isUploadingImage = false;
        console.error('Lỗi khi tải ảnh lên thư viện:', err);
        this.toastr.error('Không thể tải ảnh lên thư viện', 'Lỗi');
        this.cdr.detectChanges();
      }
    );
    this.subs.push(sub);

    event.target.value = '';
  }

  onUploadMultipleImages(event: any): void {
    const files: File[] = Array.from(event.target.files);
    if (!files || files.length === 0) return;

    this.isUploadingImages = true;
    this.cdr.detectChanges();

    const sub = this.equipmentService.uploadEquipmentImages(this.equipment.id, files).subscribe(
      (results) => {
        this.isUploadingImages = false;
        this.hasUploadedImage = true;
        this.toastr.success(`Đã tải lên ${files.length} ảnh thành công`, 'Thành công');
        this.refreshEquipmentData();
        this.cdr.detectChanges();
      },
      (err) => {
        this.isUploadingImages = false;
        console.error('Lỗi khi tải nhiều ảnh lên:', err);
        this.toastr.error('Không thể tải ảnh lên thiết bị', 'Lỗi');
        this.cdr.detectChanges();
      }
    );
    this.subs.push(sub);

    event.target.value = '';
  }

  onDeleteImage(imageId: string, event: Event): void {
    event.stopPropagation();
    if (!imageId) return;

    this.isDeletingImage = true;
    this.cdr.detectChanges();

    const sub = this.equipmentService.deleteEquipmentImage(imageId).subscribe(
      (res) => {
        this.isDeletingImage = false;
        this.equipmentImages = this.equipmentImages.filter(img => img.id !== imageId);
        if (this.selectedGalleryIndex >= this.equipmentImages.length) {
          this.selectedGalleryIndex = Math.max(0, this.equipmentImages.length - 1);
        }
        this.toastr.success('Đã xóa ảnh thành công', 'Thành công');
        this.hasUploadedImage = true;
        this.cdr.detectChanges();
      },
      (err) => {
        this.isDeletingImage = false;
        console.error('Lỗi khi xóa ảnh:', err);
        this.toastr.error('Không thể xóa ảnh', 'Lỗi');
        this.cdr.detectChanges();
      }
    );
    this.subs.push(sub);
  }

  selectGalleryImage(index: number): void {
    this.selectedGalleryIndex = index;
    this.cdr.detectChanges();
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Đã giao':
        return 'status-delivered';
      case 'Đang xử lý':
        return 'status-processing';
      case 'Đã huỷ':
        return 'status-cancelled';
      default:
        return '';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'active':
        return 'Đang hoạt động';
      case 'inactive':
        return 'Ngừng hoạt động';
      case 'maintenance':
        return 'Bảo trì';
      case 'retired':
        return 'Ngừng sử dụng';
      default:
        return status;
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'active':
        return 'status-badge-active';
      case 'inactive':
        return 'status-badge-inactive';
      case 'maintenance':
        return 'status-badge-maintenance';
      case 'retired':
        return 'status-badge-retired';
      default:
        return '';
    }
  }

  onStatusChange(newStatus: string): void {
    if (!newStatus || newStatus === this.equipment.status) {
      return;
    }

    this.isUpdatingStatus = true;
    this.cdr.detectChanges();

    const updatedEquipment: Equipment = {
      ...this.equipment,
      status: newStatus
    };

    const sub = this.equipmentService.updateEquipment(this.equipment.id, updatedEquipment).subscribe(
      (res) => {
        this.equipment.status = newStatus;
        this.isUpdatingStatus = false;
        this.toastr.success('Cập nhật trạng thái thành công', 'Thành công');
        this.cdr.detectChanges();
      },
      (err) => {
        this.isUpdatingStatus = false;
        console.error('Lỗi cập nhật trạng thái:', err);
        this.toastr.error('Không thể cập nhật trạng thái', 'Lỗi');
        this.cdr.detectChanges();
      }
    );
    this.subs.push(sub);
  }

  onEditEquipment(): void {
    const editDialogRef = this.dialog.open(AddEquipmentDialogComponent, {
      width: '95vw',
      maxWidth: '1100px',
      maxHeight: '90vh',
      panelClass: 'add-equipment-dialog',
      data: {
        equipment: this.equipment
      }
    });

    editDialogRef.afterClosed().subscribe((updatedEq: Equipment | null) => {
      if (updatedEq) {
        this.equipment = updatedEq;
        this.hasUploadedImage = true;
        this.cdr.detectChanges();
      }
    });
  }

  onClose(): void {
    this.dialogRef.close({ refresh: this.hasUploadedImage });
  }
}
